import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    Box,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { CriteriaTypeModal } from '../CriteriaTypeModal/CriteriaTypeModal';
import { CTG } from './criteriaTypeGrid.classes';

import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { RuleServiceCommand } from '../../../../../../../../services/command';

export const CriteriaTypeGrid: FC<any> = () => {
    const { gridData, totalCount, setPage, page, rowsPerPage, setRowsPerPage, fetchCriteriaTypeGridData } =
        useRuleService();

    const [openDialog, setOpenDialog] = useState(false);
    const [selectedCriteriaValues, setSelectedCriteriaValues] = useState<any[]>([]);

    const [selectedCriteriaType, setSelectedCriteriaType] = useState<any>();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editCriteriaType, setEditCriteriaType] = useState<any>(null);

    const { t } = useTranslation();
    const { tenant } = useRuleService();

    const handleChangePage = (_: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleOpenDialog = (criteriaType: any) => {
        setSelectedCriteriaValues(criteriaType.criteriaTypeValues);
        setSelectedCriteriaType(criteriaType);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCriteriaValues([]);
    };

    const handleEditCriteriaType = (criteriaType: any) => {
        setEditCriteriaType(criteriaType);
        setIsEditModalOpen(true);
    };

    const handleDeleteCriteriaType = async (id: number) => {
        const result = await Swal.fire({
            title: t('general.delete'),
            text: t('general.deleteConfirmationText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        });
        if (result.isConfirmed) {
            try {
                const res = await RuleServiceCommand.CriteriaTypeDelete({ Id: id });
                if (res && res.statuscode !== 200 && res.StatusCode !== 200) {
                    Swal.fire(t('general.error'), res?.error || res?.message || t('general.couldntDeleted'), 'error');
                    return;
                }
                Swal.fire(t('general.success'), t('general.deleteOperationSuccessMessage'), 'success');
                fetchCriteriaTypeGridData();
            } catch (error: any) {
                const errorMsg =
                    error?.data?.error || error?.data?.message || error?.message || t('general.couldntDeleted');
                Swal.fire(t('general.error'), errorMsg, 'error');
            }
        }
    };

    useEffect(() => {
        fetchCriteriaTypeGridData();
    }, [tenant, rowsPerPage, page, fetchCriteriaTypeGridData]);

    return (
        <>
            <Paper className={CTG.tablePaper}>
                <Table className={CTG.table}>
                    <TableHead className={CTG.head}>
                        <TableRow className={CTG.headRow}>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}>
                                {t('ruleManagement.fields.criteriaType.id.name')}
                            </TableCell>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}>
                                {t('ruleManagement.fields.criteriaType.tenant.name')}
                            </TableCell>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}>
                                {t('ruleManagement.fields.criteriaType.name.name')}
                            </TableCell>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}>
                                {t('ruleManagement.fields.criteriaType.key.name')}
                            </TableCell>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}>
                                {t('ruleManagement.fields.criteriaType.child.name')}
                            </TableCell>
                            <TableCell className={CTG.headCell} style={{ textAlign: 'center' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={CTG.body}>
                        {(gridData || []).map((type: any, key: number) => (
                            <TableRow className={CTG.row} key={key} data-criteria-type-id={type.id}>
                                <TableCell className={[CTG.cell, CTG.cellId].join(' ')} style={{ textAlign: 'center' }}>
                                    {type.id}
                                </TableCell>
                                <TableCell
                                    className={[CTG.cell, CTG.cellTenant].join(' ')}
                                    style={{ textAlign: 'center' }}
                                >
                                    {RuleTenant[type.tenant as keyof typeof RuleTenant]}
                                </TableCell>
                                <TableCell className={[CTG.cell, CTG.cellName].join(' ')} style={{ textAlign: 'center' }}>
                                    {type.name}
                                </TableCell>
                                <TableCell className={[CTG.cell, CTG.cellKey].join(' ')} style={{ textAlign: 'center' }}>
                                    {type.key}
                                </TableCell>
                                <TableCell
                                    className={[CTG.cell, CTG.cellValuesCount].join(' ')}
                                    style={{ textAlign: 'center' }}
                                >
                                    {type.criteriaTypeValues?.length > 0 && (
                                        <Chip
                                            className={CTG.valuesChip}
                                            sx={{ marginLeft: '4px' }}
                                            label={`${type.criteriaTypeValues?.length}`}
                                            onClick={() => handleOpenDialog(type)}
                                            color={'warning'}
                                        />
                                    )}
                                </TableCell>

                                <TableCell
                                    className={[CTG.cell, CTG.cellActions].join(' ')}
                                    style={{ textAlign: 'center' }}
                                >
                                    <IconButton
                                        className={CTG.editButton}
                                        color="primary"
                                        onClick={() => handleEditCriteriaType(type)}
                                    >
                                        <EditIcon className={CTG.editIcon} sx={{ width: '16px', height: '16px' }} />
                                    </IconButton>
                                    <IconButton
                                        className={CTG.deleteButton}
                                        color="error"
                                        onClick={() => handleDeleteCriteriaType(type.id)}
                                    >
                                        <DeleteOutlineIcon
                                            className={CTG.deleteIcon}
                                            sx={{ width: '16px', height: '16px' }}
                                        />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            <TablePagination
                className={CTG.pagination}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Dialog className={CTG.detailsDialog} open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle className={CTG.detailsDialogTitle}>
                    <Box
                        className={CTG.detailsDialogTitleLayout}
                        sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', width: '100%' }}
                    >
                        {t('ruleManagement.fields.criteriaType.details.nameV2') + ' - '}
                        <span className={CTG.detailsDialogTitleName} style={{ color: 'blue' }}>
                            {`(${selectedCriteriaType?.name})`}
                        </span>
                        <IconButton className={CTG.detailsDialogCloseButton} sx={{ marginLeft: 'auto' }}>
                            <CloseIcon
                                className={['svg_icons', CTG.detailsDialogCloseIcon].filter(Boolean).join(' ')}
                                onClick={handleCloseDialog}
                            />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={CTG.detailsDialogContent}>
                    <Table className={CTG.detailsTable}>
                        <TableHead className={CTG.detailsTableHead}>
                            <TableRow className={CTG.detailsTableHeadRow}>
                                <TableCell className={CTG.detailsTableHeadCell} style={{ textAlign: 'center' }}>
                                    {t('ruleManagement.fields.criteriaType.preDefParameter.name')}
                                </TableCell>
                                <TableCell className={CTG.detailsTableHeadCell} style={{ textAlign: 'center' }}>
                                    {t('ruleManagement.fields.criteriaType.child.value')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className={CTG.detailsTableBody}>
                            {(selectedCriteriaValues || []).map((item: any, index: number) => (
                                <TableRow className={CTG.detailsTableRow} key={index} data-value-index={index}>
                                    <TableCell className={CTG.detailsTableCell} style={{ textAlign: 'center' }}>
                                        {item.name}
                                    </TableCell>
                                    <TableCell className={CTG.detailsTableCell} style={{ textAlign: 'center' }}>
                                        {item.value}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>
            <CriteriaTypeModal
                isModalOpen={isEditModalOpen}
                setIsModalOpen={setIsEditModalOpen}
                initialData={editCriteriaType}
            />
        </>
    );
};
